/**
 * Lead Detail Page - ISOCNTRL Redesign
 * Comprehensive lead view with integrated Calendar, Tasks, Email, Files
 */
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  ArrowLeft, Mail, Phone, MapPin, Calendar as CalendarIcon, 
  CheckSquare, FileText, Activity, Edit, Trash2, Plus,
  Clock, User, Send, Loader2, Eye, ChevronDown, ChevronUp,
  Paperclip, X as XIcon, Download, Briefcase, GitBranch, ArrowRightLeft
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// SEC-003: files open through a short-lived URL issued only after server-side authorization
const openSignedFile = async (fileUrl) => {
  try {
    const { url } = await api.request('/files-sign', { method: 'POST', body: JSON.stringify({ path: fileUrl }) });
    window.open(`${API_BASE}${url}`, '_blank', 'noopener,noreferrer');
  } catch {
    toast.error('You do not have access to this file');
  }
};

import { MerchantContextPanel } from '../components/MerchantContextPanel';
import { ComposeEmail } from '../components/communications/ComposeEmail';
import { SyncedEmails } from '../components/comm/GmailSync';

const PRODUCT_TYPE_LABELS = {
  payments_processing: 'Payments Processing',
  pos_system: 'POS System',
  lending: 'Lending',
  equipment_financing: 'Equipment Financing',
  ecommerce: 'E-Commerce',
  other: 'Other',
};


export default function LeadDetailPage() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  
  const [lead, setLead] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [meetings, setMeetings] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [notes, setNotes] = useState([]);
  const [emails, setEmails] = useState([]);
  const [documents, setDocuments] = useState([]);
  
  // Dialog states
  const [meetingDialogOpen, setMeetingDialogOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [emailComposeOpen, setEmailComposeOpen] = useState(false);

  // Email compose state
  const [composeSubject, setComposeSubject] = useState('');
  const [composeScheduleAt, setComposeScheduleAt] = useState('');
  const [composeSending, setComposeSending] = useState(false);
  const [expandedEmailId, setExpandedEmailId] = useState(null);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [hasBodyContent, setHasBodyContent] = useState(false);
  const [convertingToMerchant, setConvertingToMerchant] = useState(false);
  const [opportunities, setOpportunities] = useState([]);
  const [addingOpportunity, setAddingOpportunity] = useState(false);
  const [newOppPipeline, setNewOppPipeline] = useState('merchant_services');
  const [newOppProduct, setNewOppProduct] = useState('');
  const [savingOpp, setSavingOpp] = useState(false);
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Form states
  const [newMeeting, setNewMeeting] = useState({
    title: '',
    description: '',
    start_time: '',
    end_time: '',
    location: ''
  });
  
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    due_date: '',
    priority: 'normal'
  });
  
  const [newNote, setNewNote] = useState('');

  useEffect(() => {
    loadAllData();
  }, [leadId]);

  const loadAllData = async () => {
    try {
      setLoading(true);
      const [leadData, meetingsData, documentsData] = await Promise.all([
        api.getLead(leadId),
        loadMeetings(),
        api.getLeadDocuments(leadId)
      ]);
      
      setLead(leadData);
      setDocuments(documentsData);
      loadEmails();
      loadEmailTemplates();
      loadOpportunities();
    } catch (error) {
      console.error('Failed to load lead data:', error);
      toast.error('Failed to load lead details');
    } finally {
      setLoading(false);
    }
  };

  const loadEmails = async () => {
    try {
      const resp = await fetch(`${API_BASE}/api/leads/${leadId}/emails`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setEmails(data.emails || []);
      }
    } catch (e) {
      console.error('Failed to load emails:', e);
    }
  };

  const loadEmailTemplates = async () => {
    try {
      const resp = await fetch(`${API_BASE}/api/email/templates`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setEmailTemplates(data.templates || []);
      }
    } catch (e) {
      console.error('Failed to load templates:', e);
    }
  };

  const loadOpportunities = async () => {
    try {
      const resp = await fetch(`${API_BASE}/api/pipeline/leads/${leadId}/opportunities`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setOpportunities(data.opportunities || []);
      }
    } catch (e) {
      console.error('Failed to load opportunities:', e);
    }
  };

  const handleAddOpportunity = async () => {
    if (!newOppPipeline) return;
    setSavingOpp(true);
    try {
      const resp = await fetch(`${API_BASE}/api/pipeline/deals`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          pipeline: newOppPipeline,
          merchant_name: lead.business_name,
          lead_id: leadId,
          product_type: newOppProduct || newOppPipeline,
          source: lead.source || '',
          monthly_volume: lead.monthly_volume || 0,
        })
      });
      if (!resp.ok) throw new Error('Failed to create opportunity');
      toast.success('Opportunity created and linked to this lead');
      setAddingOpportunity(false);
      setNewOppPipeline('credit_card');
      setNewOppProduct('');
      loadOpportunities();
    } catch (e) {
      toast.error('Could not create opportunity');
    } finally {
      setSavingOpp(false);
    }
  };

  const loadMeetings = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/leads/${leadId}/meetings`,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }
      );
      if (!response.ok) {
        setMeetings([]);
        return [];
      }
      const data = await response.json();
      setMeetings(data.meetings || []);
      return data.meetings || [];
    } catch (error) {
      console.error('Failed to load meetings:', error);
      setMeetings([]);
      return [];
    }
  };

  const handleBookMeeting = async () => {
    try {
      const meetingData = {
        title: newMeeting.title,
        description: newMeeting.description,
        location: newMeeting.location,
        start_time: new Date(newMeeting.start_time).toISOString(),
        end_time: new Date(newMeeting.end_time).toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      };

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/leads/${leadId}/meetings`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(meetingData)
        }
      );

      if (response.ok) {
        toast.success('Meeting scheduled!', {
          description: `Calendar invite sent to ${lead.email}`
        });
        setMeetingDialogOpen(false);
        setNewMeeting({ title: '', description: '', start_time: '', end_time: '', location: '' });
        loadMeetings();
      } else {
        const error = await response.json();
        toast.error('Failed to schedule meeting', { description: error.detail });
      }
    } catch (error) {
      console.error('Failed to book meeting:', error);
      toast.error('Failed to schedule meeting');
    }
  };

  const handleLaunchApplication = async () => {
    if (lead.application_id) {
      navigate(`/applications/${lead.application_id}`);
      return;
    }
    
    try {
      const app = await api.createApplication(leadId);
      toast.success('Application created!');
      navigate(`/applications/${app.id}`);
    } catch (error) {
      console.error('Failed to create application:', error);
      toast.error('Failed to launch application');
    }
  };

  const handleConvertToMerchant = async () => {
    if (lead.merchant_group_id) {
      navigate(`/merchants/${lead.merchant_group_id}`);
      return;
    }
    setConvertingToMerchant(true);
    try {
      const res = await fetch(`${API_BASE}/api/merchants/from-lead/${leadId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      // Contract #10 — a prospect is not a merchant yet.
      if (res.status === 409 && data.detail?.error === 'lead_not_ready_for_merchant_vault') {
        toast.error(data.detail.message, { duration: 8000 });
        return;
      }
      if (data.status === 'duplicate_detected') {
        toast.info('Potential duplicate merchant found — review matches', { duration: 5000 });
        navigate(`/merchants`);
      } else if (data.status === 'created') {
        toast.success(`Merchant "${lead.business_name}" created in vault!`);
        navigate(`/merchants/${data.merchant_group_id}`);
      } else {
        toast.error(data.detail || 'Conversion failed');
      }
    } catch (error) {
      console.error('Convert to merchant failed:', error);
      toast.error('Failed to convert lead to merchant');
    } finally {
      setConvertingToMerchant(false);
    }
  };

  const handleSendEmail = async () => {
    const body = editorRef.current?.innerHTML || '';
    if (!composeSubject.trim() || !body.trim() || body === '<br>') return;
    setComposeSending(true);
    try {
      const resp = await fetch(`${API_BASE}/api/email/compose`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          lead_id: leadId,
          to_email: lead.email,
          to_name: lead.contact_name,
          subject: composeSubject,
          body: body,
          attachments: attachments.length > 0 ? attachments : null,
          schedule_at: composeScheduleAt || null,
        }),
      });
      if (resp.ok) {
        toast.success(composeScheduleAt ? 'Email scheduled!' : 'Email sent!');
        setEmailComposeOpen(false);
        setComposeSubject('');
        setComposeScheduleAt('');
        setSelectedTemplate('');
        setAttachments([]);
        setHasBodyContent(false);
        if (editorRef.current) editorRef.current.innerHTML = '';
        loadEmails();
      } else {
        const err = await resp.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to send email');
      }
    } catch (e) {
      toast.error('Failed to send email');
    } finally {
      setComposeSending(false);
    }
  };

  const handleTemplateSelect = async (templateId) => {
    setSelectedTemplate(templateId);
    if (!templateId) return;
    const tmpl = emailTemplates.find(t => t.template_id === templateId);
    if (tmpl) {
      let subject = tmpl.subject;
      let body = tmpl.html_body || '';
      const vars = {
        business_name: lead.business_name || '',
        contact_name: lead.contact_name || '',
        monthly_savings: '$---',
        annual_savings: '$---',
        proposal_url: '#',
        expiration_hours: '72',
        agent_name: 'Your Representative',
        agent_phone: '(888) 555-0199',
      };
      Object.entries(vars).forEach(([k, v]) => {
        subject = subject.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), v);
        body = body.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), v);
      });
      setComposeSubject(subject);
      if (editorRef.current) editorRef.current.innerHTML = body;
      setHasBodyContent(true);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        const resp = await fetch(`${API_BASE}/api/email/upload-attachment`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          body: formData,
        });
        if (resp.ok) {
          const data = await resp.json();
          setAttachments(prev => [...prev, data]);
        } else {
          toast.error(`Failed to upload ${file.name}`);
        }
      }
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (attachmentId) => {
    setAttachments(prev => prev.filter(a => a.attachment_id !== attachmentId));
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const execCmd = (cmd, val) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
  };

  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || '').substring(0, 150);
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-green-100 text-green-800 border-green-200',
      contacted: 'bg-blue-100 text-blue-800 border-blue-200',
      qualified: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      proposal_sent: 'bg-purple-100 text-purple-800 border-purple-200',
      converted: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      lost: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[status] || colors.new;
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading lead details...</p>
        </div>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="p-8">
        <Card className="p-12 text-center">
          <p className="text-muted-foreground">Lead not found</p>
          <Button onClick={() => navigate('/leads')} variant="outline" className="mt-4">
            Back to Leads
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Back Button */}
      <Button 
        variant="ghost" 
        onClick={() => navigate('/leads')}
        className="mb-4"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Leads
      </Button>

      {/* Header Card */}
      <Card className="p-6 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-4 mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-[#3b82f6] to-[#60a5fa] rounded-xl flex items-center justify-center text-white text-2xl font-bold">
                {lead.contact_name?.charAt(0) || 'L'}
              </div>
              <div>
                <h1 className="text-3xl font-bold">{lead.business_name}</h1>
                {lead.unsubscribed_at && (
                  <span className="mt-1 inline-flex items-center rounded-full border border-red-300 bg-red-50 px-2 py-0.5 text-xs text-red-700" data-testid="lead-unsubscribed-badge"
                    title="Marketing emails and campaigns skip this contact">
                    Unsubscribed {new Date(lead.unsubscribed_at).toLocaleDateString()} · {{ reply: 'replied STOP', link: 'unsubscribe link', one_click: 'one-click unsubscribe', import: 'already unsubscribed' }[lead.unsubscribe_source] || lead.unsubscribe_source}
                  </span>
                )}
                {(lead.street || lead.city || lead.zip) && (
                  <p className="mt-1 text-sm text-muted-foreground" data-testid="lead-address">{[lead.street, lead.city, [lead.state, lead.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')}</p>
                )}
                {lead.custom_fields && Object.keys(lead.custom_fields).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground" data-testid="lead-custom-fields">
                    {Object.entries(lead.custom_fields).map(([k, v]) => <span key={k}><span className="capitalize">{k.replace(/_/g, ' ')}</span>: <b className="text-foreground">{String(v)}</b></span>)}
                  </div>
                )}
                <p className="text-xl text-muted-foreground mt-1">{lead.contact_name}</p>
              </div>
              <Badge className={`${getStatusColor(lead.status)} px-4 py-1 text-sm`}>
                {lead.status?.replace('_', ' ')}
              </Badge>
            </div>
            
            <div className="flex flex-wrap gap-6 text-sm text-muted-foreground">
              {lead.email && (
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4" />
                  <a href={`mailto:${lead.email}`} className="hover:text-foreground">
                    {lead.email}
                  </a>
                </div>
              )}
              
              {lead.phone && (
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4" />
                  <a href={`tel:${lead.phone}`} className="hover:text-foreground">
                    {lead.phone}
                  </a>
                </div>
              )}
              
              {lead.location && (
                <div className="flex items-center space-x-2">
                  <MapPin className="h-4 w-4" />
                  <span>{lead.location}</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Quick Actions */}
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" onClick={() => setComposeOpen(true)} data-testid="lead-compose-email-btn">
              Email
            </Button>
            {composeOpen && <ComposeEmail leadId={leadId} onClose={() => setComposeOpen(false)} />}
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setMeetingDialogOpen(true)}
              data-testid="book-meeting-btn"
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              Book Meeting
            </Button>
            
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setTaskDialogOpen(true)}
            >
              <CheckSquare className="mr-2 h-4 w-4" />
              Add Task
            </Button>
            
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setNoteDialogOpen(true)}
            >
              <FileText className="mr-2 h-4 w-4" />
              Add Note
            </Button>
            
            <Button 
              onClick={handleLaunchApplication}
              className="bg-[#3b82f6] hover:bg-[#2563eb]"
              data-testid="launch-application-btn"
            >
              {lead.application_id ? 'View Application' : 'Launch Application'}
            </Button>
            <Button
              onClick={handleConvertToMerchant}
              variant={lead.merchant_group_id ? 'outline' : 'default'}
              className={lead.merchant_group_id ? '' : 'bg-emerald-600 hover:bg-emerald-700'}
              disabled={convertingToMerchant}
              data-testid="convert-to-merchant-btn"
            >
              {lead.merchant_group_id ? 'View Merchant' : convertingToMerchant ? 'Converting...' : 'Convert to Merchant'}
            </Button>
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="bg-card border">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="opportunities">
            <Briefcase className="h-3.5 w-3.5 mr-1.5" />
            Opportunities {opportunities.length > 0 && <span className="ml-1 bg-primary/10 text-primary text-xs rounded-full px-1.5">{opportunities.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="notes">Notes</TabsTrigger>
          <TabsTrigger value="tasks">Tasks</TabsTrigger>
          <TabsTrigger value="meetings">Meetings</TabsTrigger>
          <TabsTrigger value="emails">Email Thread</TabsTrigger>
          <TabsTrigger value="files">Files</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Canonical Merchant Context Panel (Step 4) */}
          <MerchantContextPanel businessId={lead.business_id} recordType="lead" recordId={lead.id} />

          <div className="grid gap-6 lg:grid-cols-2">
            {/* Contact Information */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
              <div className="space-y-3">
                <div>
                  <Label className="text-muted-foreground text-xs">Contact Name</Label>
                  <p className="font-medium">{lead.contact_name || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Business Name</Label>
                  <p className="font-medium">{lead.business_name || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Email</Label>
                  <p className="font-medium">{lead.email || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Phone</Label>
                  <p className="font-medium">{lead.phone || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Source</Label>
                  <p className="font-medium">{lead.source || 'N/A'}</p>
                </div>
              </div>
            </Card>

            {/* Business Information */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Business Details</h3>
              <div className="space-y-3">
                <div>
                  <Label className="text-muted-foreground text-xs">Business Type</Label>
                  <p className="font-medium">{lead.business_type || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Current System</Label>
                  <p className="font-medium">{lead.current_system || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Monthly Volume</Label>
                  <p className="font-medium">{lead.monthly_volume ? `$${lead.monthly_volume.toLocaleString()}` : 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Pain Points</Label>
                  <p className="font-medium">{lead.pain_point || 'N/A'}</p>
                </div>
                {lead.originator_user_id && (
                  <div>
                    <Label className="text-muted-foreground text-xs">Originator</Label>
                    <p className="font-medium text-sm font-mono" data-testid="overview-originator">
                      {lead.originator_user_id.slice(-8)}
                    </p>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </TabsContent>

        {/* Opportunities Tab — CONFLICT-1 resolution */}
        <TabsContent value="opportunities">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold">Opportunities</h3>
                <p className="text-sm text-muted-foreground mt-0.5">Pipeline deals linked to this business</p>
              </div>
              <Button
                size="sm"
                onClick={() => setAddingOpportunity(true)}
                data-testid="add-opportunity-btn"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Opportunity
              </Button>
            </div>

            {/* Originator / Owner attribution (CONFLICT-2) */}
            <div className="flex flex-wrap gap-4 mb-6 p-3 bg-muted/40 rounded-lg text-sm">
              <div className="flex items-center gap-2">
                <GitBranch className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Originator:</span>
                <span className="font-medium" data-testid="lead-originator">
                  {lead.originator_user_id ? `User ${lead.originator_user_id.slice(-6)}` : 'N/A'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <ArrowRightLeft className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Current Owner:</span>
                <span className="font-medium" data-testid="lead-current-owner">
                  {lead.current_owner_user_id ? `User ${lead.current_owner_user_id.slice(-6)}` : 'N/A'}
                </span>
              </div>
            </div>

            {/* Add Opportunity Form */}
            {addingOpportunity && (
              <div className="border rounded-lg p-4 mb-6 bg-muted/20 space-y-3" data-testid="add-opp-form">
                <h4 className="font-medium text-sm">New Opportunity</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Pipeline *</Label>
                    <Select value={newOppPipeline} onValueChange={setNewOppPipeline}>
                      <SelectTrigger className="mt-1" data-testid="opp-pipeline-select">
                        <SelectValue placeholder="Select pipeline" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="merchant_services">Merchant Services</SelectItem>
                        <SelectItem value="pos">POS / Equipment</SelectItem>
                        <SelectItem value="lending">Lending / MCA</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Product Label (optional)</Label>
                    <Input
                      className="mt-1"
                      placeholder="e.g. Clover POS, MCA $50k"
                      value={newOppProduct}
                      onChange={e => setNewOppProduct(e.target.value)}
                      data-testid="opp-product-input"
                    />
                  </div>
                </div>
                <div className="flex gap-2 pt-1">
                  <Button size="sm" onClick={handleAddOpportunity} disabled={savingOpp} data-testid="save-opp-btn">
                    {savingOpp ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                    Create Opportunity
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setAddingOpportunity(false)}>Cancel</Button>
                </div>
              </div>
            )}

            {/* Opportunities List */}
            {opportunities.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground" data-testid="no-opportunities">
                <Briefcase className="h-8 w-8 mx-auto mb-3 opacity-40" />
                <p className="text-sm">No opportunities yet.</p>
                <p className="text-xs mt-1">Click "Add Opportunity" to start a pipeline deal for this merchant.</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="opportunities-list">
                {opportunities.map(opp => (
                  <div
                    key={opp.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/30 transition-colors cursor-pointer"
                    onClick={() => navigate(`/pipeline`)}
                    data-testid={`opportunity-row-${opp.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                        <Briefcase className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{opp.product_label || PRODUCT_TYPE_LABELS[opp.product_type] || opp.pipeline}</p>
                        <p className="text-xs text-muted-foreground capitalize">{opp.pipeline.replace('_', ' ')} · {opp.stage}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant={opp.status === 'won' ? 'default' : opp.status === 'lost' ? 'destructive' : 'secondary'}>
                        {opp.status}
                      </Badge>
                      {opp.monthly_volume > 0 && (
                        <span className="text-sm font-medium">${opp.monthly_volume.toLocaleString()}/mo</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold">Notes</h3>
              <Button onClick={() => setNoteDialogOpen(true)} size="sm">
                <Plus className="mr-2 h-4 w-4" />
                Add Note
              </Button>
            </div>
            
            {notes.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                <p className="text-muted-foreground">No notes yet</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-4"
                  onClick={() => setNoteDialogOpen(true)}
                >
                  Add First Note
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {notes.map((note, idx) => (
                  <div key={idx} className="border-l-4 border-[#3b82f6] pl-4 py-2">
                    <p className="text-sm mb-2">{note.content}</p>
                    <p className="text-xs text-muted-foreground">
                      By {note.created_by} • {new Date(note.created_at).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Tasks Tab */}
        <TabsContent value="tasks">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold">Tasks</h3>
              <Button onClick={() => setTaskDialogOpen(true)} size="sm">
                <Plus className="mr-2 h-4 w-4" />
                Add Task
              </Button>
            </div>
            
            {tasks.length === 0 ? (
              <div className="text-center py-12">
                <CheckSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                <p className="text-muted-foreground">No tasks yet</p>
                <p className="text-sm text-muted-foreground mt-2 mb-4">
                  Connect Google Tasks to sync reminders and to-dos
                </p>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setTaskDialogOpen(true)}
                >
                  Create First Task
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {tasks.map((task, idx) => (
                  <div key={idx} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center space-x-3">
                      <input type="checkbox" checked={task.completed} className="w-5 h-5" />
                      <div>
                        <p className="font-medium">{task.title}</p>
                        {task.due_date && (
                          <p className="text-sm text-muted-foreground">
                            Due: {new Date(task.due_date).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                    <Badge variant="outline">{task.priority}</Badge>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Meetings Tab */}
        <TabsContent value="meetings">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold">Meetings & Appointments</h3>
              <Button onClick={() => setMeetingDialogOpen(true)} size="sm">
                <Plus className="mr-2 h-4 w-4" />
                Schedule Meeting
              </Button>
            </div>
            
            {meetings.length === 0 ? (
              <div className="text-center py-12">
                <CalendarIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                <p className="text-muted-foreground">No meetings scheduled</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-4"
                  onClick={() => setMeetingDialogOpen(true)}
                >
                  Schedule First Meeting
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {meetings.map((meeting) => {
                  const startTime = new Date(meeting.start_time);
                  const isPast = startTime < new Date();
                  
                  return (
                    <div 
                      key={meeting.id} 
                      className={`p-4 border rounded-lg ${isPast ? 'opacity-60' : 'border-[#3b82f6]'}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold mb-2">{meeting.title}</h4>
                          {meeting.description && (
                            <p className="text-sm text-muted-foreground mb-3">{meeting.description}</p>
                          )}
                          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                            <div className="flex items-center space-x-2">
                              <Clock className="h-4 w-4" />
                              <span>
                                {startTime.toLocaleDateString()} at {startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                            {meeting.location && (
                              <div className="flex items-center space-x-2">
                                <MapPin className="h-4 w-4" />
                                <span>{meeting.location}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        {isPast ? (
                          <Badge variant="outline">Completed</Badge>
                        ) : (
                          <Badge className="bg-green-500">Upcoming</Badge>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Email Thread Tab */}
        <TabsContent value="emails">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold">Email Thread</h3>
                <p className="text-sm text-muted-foreground">{emails.length} email{emails.length !== 1 ? 's' : ''}</p>
              </div>
              <Button onClick={() => setEmailComposeOpen(true)} data-testid="compose-email-btn">
                <Send className="mr-2 h-4 w-4" />
                Compose Email
              </Button>
            </div>
            <SyncedEmails leadId={leadId} />
            
            {emails.length === 0 ? (
              <div className="text-center py-12">
                <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                <p className="text-muted-foreground mb-2">No emails sent yet</p>
                <p className="text-sm text-muted-foreground mb-4">
                  Send your first email to {lead.contact_name}
                </p>
                <Button variant="outline" onClick={() => setEmailComposeOpen(true)}>
                  Compose First Email
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {emails.map((email) => {
                  const isExpanded = expandedEmailId === email.email_id;
                  const openedAt = email.opened_at || email.last_opened_at;
                  const openCount = email.open_count || 0;
                  const formatOpenTime = (iso) => {
                    if (!iso) return null;
                    const d = new Date(iso);
                    const today = new Date();
                    const isToday = d.toDateString() === today.toDateString();
                    const timeStr = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                    return isToday ? `today at ${timeStr}` : `${d.toLocaleDateString()} at ${timeStr}`;
                  };
                  return (
                    <div key={email.email_id} className="border rounded-lg overflow-hidden" data-testid={`email-thread-${email.email_id}`}>
                      <button
                        className="w-full p-4 text-left hover:bg-muted/30 transition-colors flex items-start justify-between"
                        onClick={() => setExpandedEmailId(isExpanded ? null : email.email_id)}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Mail className="h-4 w-4 text-[#3b82f6] flex-shrink-0" />
                            <p className="font-semibold text-sm truncate">{email.subject}</p>
                            {/* Open indicator — visible at a glance */}
                            {openedAt ? (
                              <span className="flex items-center gap-1 text-[10px] font-medium text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 ml-1 flex-shrink-0" data-testid="email-opened-badge">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                                Opened {formatOpenTime(openedAt)}{openCount > 1 ? ` · ${openCount}×` : ''}
                              </span>
                            ) : (
                              <span className="text-[10px] text-muted-foreground ml-1 flex-shrink-0">Not opened yet</span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            To: {email.to_email} &middot; From: {email.from_name}
                          </p>
                          {!isExpanded && (
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                              {stripHtml(email.html_body)}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                          <Badge variant={email.status === 'sent' ? 'default' : 'secondary'} className="text-[10px]">
                            {email.status}
                          </Badge>
                          <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                            {email.sent_at ? new Date(email.sent_at).toLocaleDateString() : 'Pending'}
                          </span>
                          {isExpanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                        </div>
                      </button>
                      {isExpanded && (
                        <div className="border-t bg-muted/10">
                          <div className="p-3 border-b bg-muted/20 flex items-center justify-between">
                            <div className="text-xs text-muted-foreground space-y-0.5">
                              <p><span className="font-medium">From:</span> {email.from_name}</p>
                              <p><span className="font-medium">To:</span> {email.to_email}</p>
                              <p><span className="font-medium">Date:</span> {email.sent_at ? new Date(email.sent_at).toLocaleString() : email.created_at ? new Date(email.created_at).toLocaleString() : 'N/A'}</p>
                              {openedAt && (
                                <p className="text-emerald-600 font-medium">
                                  Email opened: {formatOpenTime(openedAt)}{openCount > 1 ? ` (${openCount} times total)` : ''}
                                </p>
                              )}
                            </div>
                            {email.industry && (
                              <Badge variant="outline" className="text-[10px]">{email.industry}</Badge>
                            )}
                          </div>
                          <div className="p-1">
                            <iframe
                              srcDoc={email.html_body || '<p style="padding:16px;color:#999;">No content</p>'}
                              title={`Email: ${email.subject}`}
                              className="w-full border-0"
                              style={{ minHeight: '300px' }}
                              sandbox=""
                            />
                          </div>
                          {email.attachments?.length > 0 && (
                            <div className="px-4 py-3 border-t bg-muted/10">
                              <p className="text-xs font-medium text-muted-foreground mb-2">Attachments ({email.attachments.length})</p>
                              <div className="flex flex-wrap gap-2">
                                {email.attachments.map((a, i) => (
                                  <button type="button" key={i} onClick={() => openSignedFile(a.file_url)} data-testid={`email-attachment-${i}`} className="flex items-center gap-1.5 bg-background rounded px-2.5 py-1.5 text-xs border hover:border-primary transition-colors">
                                    <Paperclip className="h-3 w-3 text-muted-foreground" />
                                    <span className="truncate max-w-[150px]">{a.filename}</span>
                                    <span className="text-muted-foreground">({formatFileSize(a.file_size)})</span>
                                    <Download className="h-3 w-3 text-muted-foreground" />
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {/* Compose Email Dialog */}
          <Dialog open={emailComposeOpen} onOpenChange={(v) => { setEmailComposeOpen(v); if (!v) { setSelectedTemplate(''); setAttachments([]); setHasBodyContent(false); if (editorRef.current) editorRef.current.innerHTML = ''; } }}>
            <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Compose Email</DialogTitle>
                <DialogDescription>Send an email to {lead.contact_name} ({lead.email})</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 mt-2">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs font-medium text-muted-foreground">To</Label>
                    <Input value={lead.email || ''} readOnly className="bg-muted mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-muted-foreground">Load Template (optional)</Label>
                    <Select value={selectedTemplate} onValueChange={handleTemplateSelect}>
                      <SelectTrigger className="mt-1" data-testid="compose-template-select"><SelectValue placeholder="Start from template..." /></SelectTrigger>
                      <SelectContent>
                        {emailTemplates.map(t => (
                          <SelectItem key={t.template_id} value={t.template_id}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-muted-foreground">Schedule Send (optional)</Label>
                    <Input type="datetime-local" value={composeScheduleAt} onChange={e => setComposeScheduleAt(e.target.value)} className="mt-1" />
                  </div>
                </div>

                <div>
                  <Label className="text-xs font-medium text-muted-foreground">Subject *</Label>
                  <Input value={composeSubject} onChange={e => setComposeSubject(e.target.value)} placeholder="Email subject..." className="mt-1" data-testid="compose-subject" />
                </div>

                {/* Rich Text Editor */}
                <div>
                  <Label className="text-xs font-medium text-muted-foreground mb-1 block">Message *</Label>
                  <div className="border rounded-lg overflow-hidden mt-1" data-testid="rich-text-editor">
                    {/* Formatting Toolbar */}
                    <div className="flex items-center gap-0.5 px-2 py-1.5 border-b bg-muted/40 flex-wrap">
                      <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => execCmd('bold')} title="Bold" className="w-8 h-8 flex items-center justify-center rounded hover:bg-background transition-colors font-bold text-sm" data-testid="fmt-bold">B</button>
                      <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => execCmd('italic')} title="Italic" className="w-8 h-8 flex items-center justify-center rounded hover:bg-background transition-colors italic text-sm">I</button>
                      <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => execCmd('underline')} title="Underline" className="w-8 h-8 flex items-center justify-center rounded hover:bg-background transition-colors underline text-sm">U</button>
                      <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => execCmd('strikeThrough')} title="Strikethrough" className="w-8 h-8 flex items-center justify-center rounded hover:bg-background transition-colors line-through text-sm">S</button>
                      <div className="w-px h-5 bg-border mx-1" />
                      <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => execCmd('fontSize', '5')} title="Increase font size" className="w-8 h-8 flex items-center justify-center rounded hover:bg-background transition-colors text-sm font-medium">A<sup className="text-[8px]">+</sup></button>
                      <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => execCmd('fontSize', '2')} title="Decrease font size" className="w-8 h-8 flex items-center justify-center rounded hover:bg-background transition-colors text-xs font-medium">A<sup className="text-[8px]">-</sup></button>
                      <div className="w-px h-5 bg-border mx-1" />
                      <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => execCmd('insertUnorderedList')} title="Bullet list" className="w-8 h-8 flex items-center justify-center rounded hover:bg-background transition-colors text-sm">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="9" y1="6" x2="20" y2="6"/><line x1="9" y1="12" x2="20" y2="12"/><line x1="9" y1="18" x2="20" y2="18"/><circle cx="4" cy="6" r="1.5" fill="currentColor"/><circle cx="4" cy="12" r="1.5" fill="currentColor"/><circle cx="4" cy="18" r="1.5" fill="currentColor"/></svg>
                      </button>
                      <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => execCmd('insertOrderedList')} title="Numbered list" className="w-8 h-8 flex items-center justify-center rounded hover:bg-background transition-colors text-sm">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="10" y1="6" x2="20" y2="6"/><line x1="10" y1="12" x2="20" y2="12"/><line x1="10" y1="18" x2="20" y2="18"/><text x="2" y="8" fontSize="7" fill="currentColor" stroke="none">1</text><text x="2" y="14" fontSize="7" fill="currentColor" stroke="none">2</text><text x="2" y="20" fontSize="7" fill="currentColor" stroke="none">3</text></svg>
                      </button>
                      <div className="flex-1" />
                      <button type="button" onClick={() => fileInputRef.current?.click()} title="Attach files" className="w-8 h-8 flex items-center justify-center rounded hover:bg-background transition-colors" data-testid="attach-file-btn">
                        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
                      </button>
                      <input type="file" ref={fileInputRef} multiple hidden onChange={handleFileUpload} accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt" />
                    </div>

                    {/* Editable Area */}
                    <div
                      ref={editorRef}
                      contentEditable
                      suppressContentEditableWarning
                      className="min-h-[220px] max-h-[400px] overflow-y-auto p-4 outline-none text-sm leading-relaxed focus:ring-0"
                      onInput={() => setHasBodyContent((editorRef.current?.textContent || '').trim().length > 0)}
                      data-testid="compose-body"
                      style={{ wordBreak: 'break-word' }}
                    />
                  </div>
                </div>

                {/* Attachments */}
                {attachments.length > 0 && (
                  <div className="space-y-2" data-testid="attachments-list">
                    <Label className="text-xs font-medium text-muted-foreground">Attachments ({attachments.length})</Label>
                    <div className="flex flex-wrap gap-2">
                      {attachments.map((a) => (
                        <div key={a.attachment_id} className="flex items-center gap-2 bg-muted/50 rounded-lg px-3 py-2 text-sm border">
                          <Paperclip className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                          <span className="truncate max-w-[180px]">{a.filename}</span>
                          <span className="text-xs text-muted-foreground">({formatFileSize(a.file_size)})</span>
                          <button onClick={() => removeAttachment(a.attachment_id)} className="text-muted-foreground hover:text-destructive transition-colors ml-1" data-testid={`remove-attachment-${a.attachment_id}`}>
                            <XIcon className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-2 border-t">
                  <Button variant="outline" onClick={() => setEmailComposeOpen(false)}>Cancel</Button>
                  <Button onClick={handleSendEmail} disabled={composeSending || !composeSubject.trim() || !hasBodyContent} data-testid="compose-send-btn">
                    {composeSending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                    {composeScheduleAt ? 'Schedule' : 'Send Email'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Files Tab */}
        <TabsContent value="files">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold">Documents</h3>
              <Button size="sm">
                <Plus className="mr-2 h-4 w-4" />
                Upload Document
              </Button>
            </div>
            
            {documents.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                <p className="text-muted-foreground">No documents uploaded</p>
              </div>
            ) : (
              <div className="space-y-3">
                {documents.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <p className="font-medium">{doc.filename}</p>
                      <p className="text-sm text-muted-foreground">
                        {doc.document_type} • {new Date(doc.uploaded_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Button variant="ghost" size="sm">View</Button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-6">Activity Timeline</h3>
            
            <div className="space-y-4">
              <div className="flex items-start space-x-4 pb-4 border-b">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <User className="h-5 w-5 text-green-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium">Lead created</p>
                  <p className="text-sm text-muted-foreground">
                    {new Date(lead.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
              
              {lead.updated_at && lead.updated_at !== lead.created_at && (
                <div className="flex items-start space-x-4 pb-4 border-b">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <Edit className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">Lead updated</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(lead.updated_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Book Meeting Dialog */}
      <Dialog open={meetingDialogOpen} onOpenChange={setMeetingDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Schedule Meeting with {lead.contact_name}</DialogTitle>
            <DialogDescription>
              Create a meeting and send calendar invite to {lead.email}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="meeting-title">Meeting Title *</Label>
              <Input
                id="meeting-title"
                value={newMeeting.title}
                onChange={(e) => setNewMeeting({ ...newMeeting, title: e.target.value })}
                placeholder="e.g., Product Demo Call"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="meeting-desc">Description</Label>
              <Textarea
                id="meeting-desc"
                value={newMeeting.description}
                onChange={(e) => setNewMeeting({ ...newMeeting, description: e.target.value })}
                placeholder="Meeting agenda..."
                className="mt-1"
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="meeting-start">Start Time *</Label>
                <Input
                  id="meeting-start"
                  type="datetime-local"
                  value={newMeeting.start_time}
                  onChange={(e) => setNewMeeting({ ...newMeeting, start_time: e.target.value })}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="meeting-end">End Time *</Label>
                <Input
                  id="meeting-end"
                  type="datetime-local"
                  value={newMeeting.end_time}
                  onChange={(e) => setNewMeeting({ ...newMeeting, end_time: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="meeting-location">Location</Label>
              <Input
                id="meeting-location"
                value={newMeeting.location}
                onChange={(e) => setNewMeeting({ ...newMeeting, location: e.target.value })}
                placeholder="Zoom link, phone, or address"
                className="mt-1"
              />
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
              <p className="text-blue-800">
                <strong>📧 Calendar invite will be sent to:</strong> {lead.email}
              </p>
            </div>
          </div>
          
          <div className="flex justify-end space-x-3">
            <Button variant="outline" onClick={() => setMeetingDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleBookMeeting}
              disabled={!newMeeting.title || !newMeeting.start_time || !newMeeting.end_time}
              className="bg-[#3b82f6] hover:bg-[#2563eb]"
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              Schedule Meeting
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Task Dialog */}
      <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Task for {lead.business_name}</DialogTitle>
            <DialogDescription>
              Create a reminder or to-do for this lead
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Task Title *</Label>
              <Input
                value={newTask.title}
                onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                placeholder="e.g., Follow up on pricing"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={newTask.description}
                onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                placeholder="Task details..."
                className="mt-1"
                rows={3}
              />
            </div>
            
            <div>
              <Label>Due Date</Label>
              <Input
                type="datetime-local"
                value={newTask.due_date}
                onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                className="mt-1"
              />
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
              <strong>⏰ Reminder:</strong> If no reminder time is set, you'll receive daily reminders at 9 AM until completed
            </div>
          </div>
          
          <div className="flex justify-end space-x-3">
            <Button variant="outline" onClick={() => setTaskDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              disabled={!newTask.title}
              className="bg-[#3b82f6] hover:bg-[#2563eb]"
            >
              Create Task
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Note Dialog */}
      <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Note</DialogTitle>
            <DialogDescription>
              Record important information about this lead
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label>Note</Label>
            <Textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Type your note here..."
              className="mt-2"
              rows={6}
            />
          </div>
          
          <div className="flex justify-end space-x-3">
            <Button variant="outline" onClick={() => setNoteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              disabled={!newNote.trim()}
              onClick={() => {
                // TODO: Implement note creation
                toast.success('Note added');
                setNoteDialogOpen(false);
                setNewNote('');
              }}
            >
              Save Note
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
